/**
 * Field Definition Manager Component
 *
 * Purpose: Manages field definitions with CRUD operations, filtering, and validation.
 *          Supports both GLOBAL and tenant-specific field definitions.
 *
 * Architecture: Table-based interface using MDTable component with modal forms
 *              for create/edit operations. Integrates with metadata service.
 *
 * Dependencies: React, Material-UI, MDTable, MetadataService
 * Author: Development Team
 * Created: 2025-01-27
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Typography, Chip, Tooltip, FormControlLabel, Checkbox } from '@mui/material';
import {
  Public as GlobalIcon,
  Business as TenantIcon,
  Restore as RestoreIcon,
  AccountTree as HierarchyIcon,
  Link as LinkIcon,
} from '@mui/icons-material';

import MDTable from '../../../components/md-table';
import { getScopeChipColor } from '../../../constants/mdm';
import { TableColumn } from '../../../types/table';
import metadataService, {
  FieldDefinition,
  FieldDefinitionFormData,
  Field,
  TenantInfo,
} from '../../../services/metadataService';
import { formatAuditTimestamp, formatAuditUser } from '../../../utils/formatting';
import RevertOverrideDialog from '../../../components/metadata/RevertOverrideDialog';
import FieldDefinitionModal from './FieldDefinitionModal';
import { RowActionButton, RowActions, StatusChip } from '../../../components/common';

// Styled components

// Component interfaces
interface FieldDefinitionManagerProps {
  tenantContext: TenantInfo;
  canEdit: boolean;
  userPermissions: {
    can_edit_global: boolean;
    can_edit_tenant: boolean;
    can_switch_context: boolean;
  };
  onNavigateToFields?: (modelId: string, fieldDefLabel?: string) => void;
}

/**
 * Field Definition Manager Component
 */
const FieldDefinitionManager: React.FC<FieldDefinitionManagerProps> = ({
  tenantContext,
  canEdit,
  userPermissions: _userPermissions,
  onNavigateToFields,
}) => {
  // State management
  const [data, setData] = useState<FieldDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [selectedFieldDef, setSelectedFieldDef] = useState<FieldDefinition | undefined>();

  // Revert state
  const [revertDialogOpen, setRevertDialogOpen] = useState(false);
  const [revertingFieldDef, setRevertingFieldDef] = useState<FieldDefinition | null>(null);
  const [globalFieldDef, setGlobalFieldDef] = useState<FieldDefinition | null>(null);
  const [revertLoading, setRevertLoading] = useState(false);
  const [relatedFields, setRelatedFields] = useState<Field[]>([]);

  // Filters
  const [filters, _setFilters] = useState({
    status: '',
    datatype: '',
    tenant_id: '',
  });

  // Hide GLOBAL filter - OFF by default (shows all records including GLOBAL)
  const [hideGlobal, setHideGlobal] = useState(false);

  // Load data
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Call the actual API - related fields counts are now included in the response
      const response = await metadataService.getFieldDefinitions({
        tenant_id: tenantContext.tenant_id,
        status: filters.status || undefined,
        datatype: filters.datatype || undefined,
      });
      setData(response || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load field definitions');
    } finally {
      setLoading(false);
    }
  }, [filters, tenantContext.tenant_id]);

  // Filter data based on hideGlobal setting
  const filteredData = useMemo(() => {
    if (hideGlobal) {
      return data.filter((item) => item.tenant_id !== 'GLOBAL');
    }
    return data;
  }, [data, hideGlobal]);

  // Create custom toolbar filters
  const customToolbarFilters = useMemo(
    () => (
      <FormControlLabel
        control={
          <Checkbox
            checked={hideGlobal}
            onChange={(e) => setHideGlobal(e.target.checked)}
            data-testid="hide-global-checkbox"
          />
        }
        label="Hide GLOBAL"
        sx={{
          '& .MuiFormControlLabel-label': {
            fontSize: '0.875rem',
            color: 'text.secondary',
          },
        }}
      />
    ),
    [hideGlobal]
  );

  // Load data on component mount and when filters change
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Table columns configuration
  const columns: TableColumn[] = [
    {
      field: 'label',
      headerName: 'Label',
      width: 200,
      sortable: true,
      renderCell: (value: string, row: FieldDefinition) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2">{value}</Typography>
          {row.hierarchy_config && 'hierarchy_label' in row.hierarchy_config && (
            <Tooltip title={`Hierarchy: ${row.hierarchy_config.hierarchy_label || 'Hierarchical'}`}>
              <HierarchyIcon fontSize="small" color="primary" />
            </Tooltip>
          )}
        </Box>
      ),
    },
    {
      field: 'fielddef_id',
      headerName: 'Field Def ID',
      width: 200,
      sortable: true,
      renderCell: (value: string) => (
        <Box sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }} data-testid="field-id-cell">
          {value}
        </Box>
      ),
    },
    {
      field: 'tenant_id',
      headerName: 'Scope',
      width: 120,
      sortable: true,
      renderCell: (value: string, row: FieldDefinition) => {
        // Use scope_info from API response if available, otherwise fall back to tenant_id logic
        const scopeInfo = (row as any).scope_info;
        let scopeLabel = '';
        let scopeIcon = <TenantIcon fontSize="small" />;

        if (scopeInfo) {
          // Use scope_info from backend
          scopeLabel = scopeInfo.scope;
          // Note: Backend might return 'Overridden' instead of 'OVERRIDDEN'
          if (scopeLabel === 'Overridden') {
            scopeLabel = 'OVERRIDDEN';
          }
          scopeIcon =
            scopeLabel === 'GLOBAL' ? (
              <GlobalIcon fontSize="small" />
            ) : (
              <TenantIcon fontSize="small" />
            );
        } else {
          // Fallback to tenant_id logic
          if (value === 'GLOBAL') {
            scopeLabel = 'GLOBAL';
            scopeIcon = <GlobalIcon fontSize="small" />;
          } else {
            scopeLabel = 'LOCAL';
            scopeIcon = <TenantIcon fontSize="small" />;
          }
        }

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {scopeIcon}
            <Chip
              label={scopeLabel}
              size="small"
              color={getScopeChipColor(scopeLabel)}
              variant="outlined"
            />
          </Box>
        );
      },
    },
    {
      field: 'datatype',
      headerName: 'Data Type',
      width: 120,
      sortable: true,
      renderCell: (value: string) => (
        <Chip label={metadataService.getDatatypeLabel(value)} size="small" variant="outlined" />
      ),
    },
    {
      field: 'is_multiselect',
      headerName: 'Multi-select',
      width: 100,
      sortable: true,
      renderCell: (value: boolean) => (
        <Chip
          label={value ? 'Yes' : 'No'}
          size="small"
          color={value ? 'success' : 'default'}
          variant="outlined"
        />
      ),
    },
    {
      field: 'description',
      headerName: 'Description',
      width: 200,
      sortable: true,
      renderCell: (value: string) => (
        <Typography variant="body2" color="textSecondary">
          {value || '—'}
        </Typography>
      ),
    },
    {
      field: 'creation_access',
      headerName: 'Creation Access',
      width: 140,
      sortable: true,
      renderCell: (value: string) => <Chip label={value || '—'} size="small" variant="outlined" />,
    },
    {
      field: 'storage_decimal_places',
      headerName: 'Storage Decimal Places',
      width: 180,
      sortable: true,
      renderCell: (value: number) => (
        <Typography variant="body2" color="textSecondary">
          {value !== null && value !== undefined ? value : '—'}
        </Typography>
      ),
    },
    {
      field: 'display_decimal_places',
      headerName: 'Display Decimal Places',
      width: 180,
      sortable: true,
      renderCell: (value: number) => (
        <Typography variant="body2" color="textSecondary">
          {value !== null && value !== undefined ? value : '—'}
        </Typography>
      ),
    },
    {
      field: 'related_fields_count',
      headerName: 'Related Fields',
      width: 120,
      sortable: false,
      renderCell: (_value: any, row: FieldDefinition) => {
        const isList = row.datatype === 'list';
        if (!isList) return null;

        // Use related_fields_count from the response (already included by backend)
        const count = row.related_fields_count ?? 0;

        return (
          <Chip
            label={count}
            size="small"
            color={count > 0 ? 'primary' : 'default'}
            onClick={(e) => handleRelatedFieldsClick(e, row)}
            sx={{ cursor: 'pointer' }}
          />
        );
      },
    },
    {
      field: 'hierarchy_label',
      headerName: 'Hierarchy Label',
      width: 150,
      sortable: true,
      renderCell: (_value: any, row: FieldDefinition) => {
        const label = row.hierarchy_config?.hierarchy_label;
        return (
          <Typography variant="body2" color="textSecondary">
            {label || '—'}
          </Typography>
        );
      },
    },
    {
      field: 'parent_fielddef_id',
      headerName: 'Parent Field Def',
      width: 200,
      sortable: true,
      renderCell: (_value: any, row: FieldDefinition) => {
        const parentId = row.hierarchy_config?.parent_fielddef_id;
        return (
          <Typography
            variant="body2"
            color="textSecondary"
            sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}
          >
            {parentId || '—'}
          </Typography>
        );
      },
    },
    {
      field: 'child_fielddef_id',
      headerName: 'Child Field Def',
      width: 200,
      sortable: true,
      renderCell: (_value: any, row: FieldDefinition) => {
        const childId = row.hierarchy_config?.child_fielddef_id;
        return (
          <Typography
            variant="body2"
            color="textSecondary"
            sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}
          >
            {childId || '—'}
          </Typography>
        );
      },
    },
    {
      field: 'sourcevalue_fielddef_id',
      headerName: 'Source Field',
      width: 200,
      hide: true, // Hidden by default, can be shown via column selector
      sortable: true,
      renderCell: (_value: any, row: FieldDefinition) => {
        if (!row.sourcevalue_fielddef_id) {
          return (
            <Typography variant="body2" color="textSecondary">
              —
            </Typography>
          );
        }

        const sourceLabel =
          (row as any).source_field_definition?.label || row.sourcevalue_fielddef_id;

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LinkIcon fontSize="small" color="primary" />
            <Typography variant="body2">{sourceLabel}</Typography>
          </Box>
        );
      },
    },
    // Access Level column - only show when GLOBAL tenant context is selected, and only display for list datatypes
    ...(tenantContext.is_global
      ? [
          {
            field: 'access',
            headerName: 'Access Level',
            width: 140,
            sortable: true,
            renderCell: (value: number, row: FieldDefinition) => {
              // Only show access level for list datatypes
              if (row.datatype !== 'list') {
                return (
                  <Typography variant="body2" color="textSecondary">
                    —
                  </Typography>
                );
              }
              return (
                <Chip
                  label={metadataService.getAccessLevelLabel(value)}
                  size="small"
                  color={metadataService.getAccessLevelColor(value)}
                  variant="outlined"
                />
              );
            },
          },
        ]
      : []),
    {
      field: 'updated_at',
      headerName: 'Modified Date',
      width: 140,
      sortable: true,
      renderCell: (value: string) => formatAuditTimestamp(value),
    },
    {
      field: 'updated_by',
      headerName: 'Modified By',
      width: 120,
      sortable: true,
      renderCell: (value: string, row: FieldDefinition) =>
        formatAuditUser(value, row.updated_by_user),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 100,
      sortable: true,
      renderCell: (value: string) => <StatusChip status={value} />,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (_value: any, row: FieldDefinition) => (
        <RowActions
          entityLabel="Field Definition"
          rowLabel={row.label}
          testIdPrefix="field-definition"
          rowTestId={`field-definition-row-${row.fielddef_id}`}
          onEdit={() => handleEdit(row)}
          secondaryActions={
            row.scope_info?.scope === 'Overridden' && (
              <RowActionButton
                label="Revert to Global Field Definition"
                testId="field-definition-revert"
                onClick={() => handleRevertFieldDefinition(row)}
                disabled={!canEdit}
                icon={<RestoreIcon fontSize="small" color={canEdit ? 'warning' : 'disabled'} />}
              />
            )
          }
          deactivation={{
            variant: 'disable',
            isActive: row.status !== 'disabled',
            onToggle: () => handleStatusToggle(row),
            disabled: !canEdit,
          }}
        />
      ),
    },
  ];

  // Event handlers
  const handleCreate = () => {
    setSelectedFieldDef(undefined);
    setModalMode('create');
    setModalOpen(true);
  };

  const handleEdit = (fieldDefinition: FieldDefinition) => {
    setSelectedFieldDef(fieldDefinition);
    setModalMode('edit');
    setModalOpen(true);
  };

  const handleRowClick = (row: FieldDefinition) => {
    handleEdit(row);
  };

  const handleSave = async (formData: FieldDefinitionFormData) => {
    if (modalMode === 'create') {
      await metadataService.createFieldDefinition(formData);
    } else if (selectedFieldDef) {
      // Business Rule: If editing a GLOBAL field definition from non-GLOBAL context
      if (selectedFieldDef.tenant_id === 'GLOBAL' && tenantContext.tenant_id !== 'GLOBAL') {
        // Check access level - only allow overrides for Client Overrideable (2) and Client Overrideable All (3)
        const accessLevel = selectedFieldDef.access;
        if (accessLevel === '2' || accessLevel === '3') {
          // Create a new tenant-specific override instead of modifying the GLOBAL one
          const overrideData = {
            ...formData,
            tenant_id: tenantContext.tenant_id, // Set to current tenant
          };
          await metadataService.createFieldDefinition(overrideData);
        } else {
          // This should not happen due to UI restrictions, but handle gracefully
          throw new Error('This field definition cannot be overridden in your tenant context.');
        }
      } else {
        // Normal update for non-GLOBAL or when editing in GLOBAL context
        // For tenant-specific overrides, only allow label, description, and is_multiselect updates
        let updateData: any = formData;
        if (selectedFieldDef.tenant_id !== 'GLOBAL') {
          // Tenant-specific override - only allow label, description, and is_multiselect updates
          updateData = {
            label: formData.label,
            description: formData.description,
            is_multiselect: formData.is_multiselect,
          };
        }

        await metadataService.updateFieldDefinition(
          selectedFieldDef.fielddef_id,
          selectedFieldDef.tenant_id,
          updateData,
          tenantContext.tenant_id // Pass current tenant context for access control
        );
      }
    }
    loadData();
  };

  const handleDelete = async (fieldDefinition: FieldDefinition) => {
    await metadataService.deleteFieldDefinition(
      fieldDefinition.fielddef_id,
      fieldDefinition.tenant_id
    );
    loadData();
  };

  const handleStatusToggle = async (fieldDefinition: FieldDefinition) => {
    const newStatus = fieldDefinition.status === 'disabled' ? 'active' : 'disabled';
    await metadataService.updateFieldDefinition(
      fieldDefinition.fielddef_id,
      fieldDefinition.tenant_id,
      { status: newStatus },
      tenantContext.tenant_id
    );
    loadData();
  };

  // Revert handlers
  const handleRevertFieldDefinition = useCallback(async (fieldDefinition: FieldDefinition) => {
    setRevertingFieldDef(fieldDefinition);

    // Fetch the global field definition that will become visible
    try {
      const globalFieldDef = await metadataService.getFieldDefinition(
        fieldDefinition.fielddef_id,
        'GLOBAL'
      );
      setGlobalFieldDef(globalFieldDef);

      // Fetch related fields that will be affected
      const relatedFieldsData = await metadataService.getFieldDefinitionFields(
        fieldDefinition.fielddef_id,
        fieldDefinition.tenant_id
      );
      setRelatedFields(relatedFieldsData);
    } catch (error) {
      console.error('Failed to fetch global field definition or related fields:', error);
      setGlobalFieldDef(null);
      setRelatedFields([]);
    }

    setRevertDialogOpen(true);
  }, []);

  const handleRevertConfirm = useCallback(async () => {
    if (!revertingFieldDef) return;

    setRevertLoading(true);
    try {
      await metadataService.revertFieldDefinition(
        revertingFieldDef.fielddef_id,
        revertingFieldDef.tenant_id
      );

      setRevertDialogOpen(false);
      setRevertingFieldDef(null);
      setGlobalFieldDef(null);
      setRelatedFields([]);
      // Wait for data to reload before closing loading state
      await loadData();
    } catch (error) {
      console.error('Failed to revert field definition:', error);
      // Error handling could be enhanced with a snackbar notification
    } finally {
      setRevertLoading(false);
    }
  }, [revertingFieldDef, loadData]);

  const handleRevertClose = useCallback(() => {
    setRevertDialogOpen(false);
    setRevertingFieldDef(null);
    setGlobalFieldDef(null);
    setRelatedFields([]);
    setRevertLoading(false);
  }, []);

  // Related fields handlers
  const handleRelatedFieldsClick = async (
    event: React.MouseEvent,
    fieldDefinition: FieldDefinition
  ) => {
    event.stopPropagation();

    // Navigate to Fields tab with the field definition model selected
    if (onNavigateToFields) {
      const modelId = fieldDefinition.fielddef_id;
      onNavigateToFields(modelId, fieldDefinition.label);
    }
  };

  return (
    <Box>
      {/* Data Table */}
      <Box>
        <MDTable
          mode="manual"
          manual={{
            columns,
            title: 'Field Definitions',
            description: `${filteredData.length} field definition(s) found`,
          }}
          data={filteredData.map((item) => ({
            ...item,
            id: `${item.fielddef_id}-${item.tenant_id}`,
          }))}
          loading={loading}
          error={error}
          onRowClick={handleRowClick}
          onRefresh={loadData}
          onAddNew={canEdit ? handleCreate : undefined}
          showRefreshButton={true}
          showAddButton={canEdit}
          selectable={false}
          defaultSortModel={[{ field: 'label', sort: 'asc' }]}
          sortable={true}
          filterable={true}
          customToolbarFilters={customToolbarFilters}
          defaultColumnVisibility={{
            fielddef_id: false, // Hide Field Def ID column by default
            description: false,
            creation_access: false,
            storage_decimal_places: false,
            display_decimal_places: false,
            is_multiselect: false, // Hide Multi-select column by default
            hierarchy_label: false, // Hide hierarchy-related columns by default
            parent_fielddef_id: false,
            child_fielddef_id: false,
          }}
        />
      </Box>

      {/* Create/Edit Modal */}
      <FieldDefinitionModal
        open={modalOpen}
        mode={modalMode}
        fieldDefinition={selectedFieldDef}
        tenantContext={tenantContext}
        canEdit={canEdit}
        onClose={() => setModalOpen(false)}
        onSave={handleSave}
        onDelete={handleDelete}
      />

      {/* Revert Dialog */}
      <RevertOverrideDialog
        open={revertDialogOpen}
        onClose={handleRevertClose}
        onConfirm={handleRevertConfirm}
        overrideItem={revertingFieldDef}
        globalItem={globalFieldDef}
        loading={revertLoading}
        itemType="fieldDefinition"
        relatedFields={relatedFields}
      />
    </Box>
  );
};

export default FieldDefinitionManager;
